import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight, BatteryLow, BatteryFull, Flame } from "lucide-react";
import Marquee from "@/components/Marquee";
import { Reveal, MaskedLine } from "@/components/Reveal";
import { getInsights } from "@/lib/api";

const IMG = {
  bowl: "https://images.unsplash.com/photo-1490645935967-10de6ba17061?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  salad: "https://images.unsplash.com/photo-1540420773420-3366772f4999?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  sunrise: "https://images.unsplash.com/photo-1499209974431-9dddcece7f88?crop=entropy&cs=srgb&fm=jpg&q=85&w=1300",
  training: "https://images.unsplash.com/photo-1483721310020-03333e577078?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
};

const SLUGGISH = ["Midday crash at 2:30 PM", "Brain fog & poor focus", "Irritable mood", "Erratic sleep — 5.2 hrs", "Empty-calorie nutrient voids"];
const ZINDA = ["Steady all-day energy", "Sharp mental clarity", "Deep sleep — 7.8 hrs", "Trained strength & recovery", "Food-first micronutrient shield"];

const SectionHead = ({ eyebrow, title, testid }) => (
  <div className="border-t-[3px] border-ink pt-4">
    <p className="font-sans text-xs sm:text-sm font-extrabold uppercase tracking-[0.18em] text-clay mb-2">{eyebrow}</p>
    <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-extrabold uppercase tracking-tight leading-[0.98]" data-testid={testid}>
      {title}
    </h2>
  </div>
);

function VitalitySlider() {
  const ref = useRef(null);
  const [pos, setPos] = useState(50);
  const dragging = useRef(false);

  const update = (clientX) => {
    const rect = ref.current.getBoundingClientRect();
    const p = ((clientX - rect.left) / rect.width) * 100;
    setPos(Math.min(96, Math.max(4, p)));
  };

  useEffect(() => {
    const move = (e) => dragging.current && update(e.clientX ?? e.touches?.[0]?.clientX);
    const up = () => (dragging.current = false);
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    return () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
    };
  }, []);

  return (
    <div ref={ref} className="zb-vslider relative aspect-[4/5] sm:aspect-square rounded-md overflow-hidden border-2 border-ink shadow-[8px_8px_0_#0B2530] select-none bg-sand" data-testid="hero-vitality-slider"
      onPointerDown={(e) => { dragging.current = true; update(e.clientX); }}>
      <img src="/assets/cartoons/alive.png" alt="The Zinda Banda — energized cartoon man" className="absolute inset-0 h-full w-full object-cover" draggable={false} />
      <div className="absolute inset-0" style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}>
        <img src="/assets/cartoons/couch.png" alt="The depleted state — sluggish cartoon man on couch" className="absolute inset-0 h-full w-full object-cover" draggable={false} />
      </div>
      <div className="absolute inset-y-0" style={{ left: `${pos}%` }}>
        <div className="absolute inset-y-0 -translate-x-1/2 w-[3px] bg-bone shadow-[0_0_20px_rgba(0,0,0,0.35)]" />
        <div className="absolute top-1/2 -translate-x-1/2 -translate-y-1/2 h-12 w-12 rounded-full bg-bone border-2 border-ink shadow-xl flex items-center justify-center">
          <ArrowRight className="h-4 w-4 text-ink -scale-x-100" />
          <ArrowRight className="h-4 w-4 text-ink -ml-2" />
        </div>
      </div>
      <span className="absolute top-4 left-4 font-mono text-[10px] uppercase tracking-[0.2em] bg-ink/85 text-bone px-3 py-1.5 rounded-sm flex items-center gap-1.5">
        <BatteryLow className="h-3 w-3 text-clay" /> The 2:30 PM state
      </span>
      <span className="absolute top-4 right-4 font-mono text-[10px] uppercase tracking-[0.2em] bg-sage/95 text-bone px-3 py-1.5 rounded-sm flex items-center gap-1.5">
        <BatteryFull className="h-3 w-3" /> The Zinda Banda
      </span>
    </div>
  );
}

const CHAPTERS = [
  { n: "01", kick: "Nutrition", title: "Food before pills", body: "Every plan opens on your plate — dal, sabzi, eggs, fish, curd. Supplements only enter where food cannot practically close a verified gap.", img: IMG.bowl, alt: "A high-protein whole-food bowl" },
  { n: "02", kick: "Evidence", title: "Evidence has a grade", body: "Every recommendation carries a grade from A (strong guidance) to X (suppressed on safety grounds) — plus its source, reviewer and review date.", img: IMG.salad, alt: "A micronutrient-dense salad" },
  { n: "03", kick: "Safety", title: "Safety is a gate, not a footnote", body: "On medicines, managing a condition, or heading into surgery? The plan switches to clinician-safe mode and supplement suggestions disappear entirely.", img: IMG.sunrise, alt: "A man at sunrise with outstretched arms" },
  { n: "04", kick: "Independence", title: "Commerce-free by design", body: "No affiliate links, no sponsored pills, no commission-weighted ranking. The platform stays useful with every buy button removed — because there are none.", img: IMG.training, alt: "An athlete preparing for a training session" },
];

export default function Landing() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const [insights, setInsights] = useState([]);

  useEffect(() => {
    getInsights().then((r) => setInsights(r.items || [])).catch(() => {});
  }, []);

  return (
    <main data-testid="landing-page">
      {/* HERO */}
      <section ref={heroRef} className="relative min-h-screen flex items-center pt-28 pb-16 px-4 sm:px-6 overflow-hidden zb-grain">
        <div className="mx-auto max-w-7xl w-full grid lg:grid-cols-[1.15fr_1fr] gap-12 lg:gap-16 items-center">
          <div>
            <motion.p
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1, duration: 0.6 }}
              className="font-sans text-xs sm:text-sm font-extrabold uppercase tracking-[0.18em] text-clay mb-5"
              data-testid="hero-eyebrow"
            >
              Men's vitality report — UAE &amp; India edition
            </motion.p>
            <h1 className="font-serif text-5xl sm:text-6xl lg:text-8xl font-extrabold uppercase tracking-tight leading-[0.92]" data-testid="hero-headline">
              <MaskedLine delay={0.25}>Who is</MaskedLine>
              <MaskedLine delay={0.4}><span className="text-clay">Zinda Banda?</span></MaskedLine>
            </h1>
            <motion.p
              initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.8, duration: 0.7 }}
              className="mt-6 text-base sm:text-lg text-slatem max-w-md leading-relaxed" data-testid="hero-sub"
            >
              The man whose energy outlasts his day. A 2-minute test shows how close you are —
              and a 10-point, food-first plan closes the gap.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.95, duration: 0.7 }}
              className="mt-8 flex flex-wrap items-center gap-4"
            >
              <Link to="/check-in" data-testid="hero-cta-button"
                className="group inline-flex items-center gap-2.5 rounded-md bg-clay text-bone px-7 py-3.5 font-sans text-sm sm:text-base font-bold uppercase tracking-wider transition-all duration-200 hover:bg-ink hover:gap-4 min-h-[44px]">
                Test if you are Zinda Banda
                <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" />
              </Link>
              <Link to="/method" data-testid="hero-method-link"
                className="inline-flex items-center gap-1.5 font-sans text-sm font-bold uppercase tracking-wider text-ink/70 hover:text-clay transition-colors min-h-[44px]">
                How we decide <ArrowUpRight className="h-4 w-4" />
              </Link>
            </motion.div>
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.15, duration: 0.8 }}
              className="mt-10 pt-5 border-t border-ink/15 flex flex-wrap gap-x-8 gap-y-3 font-mono text-[10px] sm:text-xs uppercase tracking-[0.2em] text-ink/60"
              data-testid="hero-stats"
            >
              <span>Evidence-graded A—X</span>
              <span>0 sponsored pills</span>
              <span>100% anonymous</span>
            </motion.div>
          </div>

          <motion.div style={{ y: imgY }} initial={{ opacity: 0, scale: 0.94 }} animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.5, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}>
            <VitalitySlider />
            <p className="mt-4 text-center font-mono text-[10px] uppercase tracking-[0.2em] text-slatem">drag to compare the two states</p>
          </motion.div>
        </div>
      </section>

      <Marquee />

      {/* CONTRAST */}
      <section className="px-4 sm:px-6 py-20 sm:py-28">
        <div className="mx-auto max-w-7xl">
          <Reveal>
            <SectionHead eyebrow="The two versions of you" title={<>Same man. Same city. <span className="text-sage">Different inputs.</span></>} testid="contrast-heading" />
          </Reveal>
          <div className="mt-12 grid md:grid-cols-2 gap-6">
            <Reveal delay={0.05}>
              <div className="rounded-md border-2 border-ink/15 bg-sand p-7 sm:p-9 h-full" data-testid="contrast-sluggish-card">
                <div className="flex items-center gap-2 mb-6">
                  <BatteryLow className="h-5 w-5 text-clay" />
                  <h3 className="font-serif text-2xl sm:text-3xl font-bold uppercase tracking-tight">The foggy &amp; depleted</h3>
                </div>
                <ul className="space-y-3.5">
                  {SLUGGISH.map((t) => (
                    <li key={t} className="flex items-center gap-3 text-sm sm:text-base text-ink/75">
                      <span className="h-1.5 w-1.5 bg-clay shrink-0" />{t}
                    </li>
                  ))}
                </ul>
                <p className="mt-7 pt-4 border-t border-ink/10 font-mono text-[10px] uppercase tracking-[0.2em] text-slatem">battery 12% — surviving, not living</p>
              </div>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="rounded-md border-2 border-ink bg-ink text-bone p-7 sm:p-9 h-full relative overflow-hidden zb-grain shadow-[8px_8px_0_rgba(11,37,48,0.15)]" data-testid="contrast-zinda-card">
                <div className="flex items-center gap-2 mb-6">
                  <BatteryFull className="h-5 w-5 text-sage" />
                  <h3 className="font-serif text-2xl sm:text-3xl font-bold uppercase tracking-tight">The Zinda Banda</h3>
                </div>
                <ul className="space-y-3.5">
                  {ZINDA.map((t) => (
                    <li key={t} className="flex items-center gap-3 text-sm sm:text-base text-bone/85">
                      <span className="h-1.5 w-1.5 bg-sage shrink-0" />{t}
                    </li>
                  ))}
                </ul>
                <p className="mt-7 pt-4 border-t border-bone/15 font-mono text-[10px] uppercase tracking-[0.2em] text-sage">battery 94% — alive, on purpose</p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* DOCTRINE */}
      <section className="px-4 sm:px-6 py-20 sm:py-28 bg-sand/60 border-y border-ink/10">
        <div className="mx-auto max-w-7xl">
          <Reveal>
            <SectionHead eyebrow="The doctrine" title="Four rules. Zero exceptions." testid="manifesto-heading" />
          </Reveal>
          <div className="mt-12 space-y-6">
            {CHAPTERS.map((c) => (
              <Reveal key={c.n} delay={0.05}>
                <div className="group grid md:grid-cols-[auto_1fr_300px] gap-6 md:gap-10 items-center rounded-md border-2 border-ink/12 bg-bone p-6 sm:p-8 transition-colors duration-300 hover:border-ink" data-testid={`manifesto-chapter-${c.n}`}>
                  <span className="font-serif text-6xl sm:text-7xl font-extrabold uppercase leading-none text-ink/12 group-hover:text-clay transition-colors duration-300">{c.n}</span>
                  <div>
                    <p className="font-sans text-[11px] font-extrabold uppercase tracking-[0.2em] text-clay mb-1.5">{c.kick}</p>
                    <h3 className="font-serif text-2xl sm:text-3xl font-bold uppercase tracking-tight leading-tight mb-2.5">{c.title}</h3>
                    <p className="text-sm sm:text-base text-slatem leading-relaxed max-w-xl">{c.body}</p>
                  </div>
                  <div className="hidden md:block overflow-hidden rounded-sm border-2 border-ink/10">
                    <img src={c.img} alt={c.alt} loading="lazy"
                      className="h-44 w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PROTOCOL */}
      <section className="px-4 sm:px-6 py-20 sm:py-28">
        <div className="mx-auto max-w-7xl">
          <Reveal>
            <SectionHead eyebrow="The protocol" title="Two minutes in. Ten points out." testid="how-heading" />
          </Reveal>
          <div className="mt-12 grid sm:grid-cols-3 gap-6">
            {[
              { n: "1", t: "Answer nine questions", d: "Routine, plate, sleep, sun, safety. One screen each. No accounts, no email, no free-text." },
              { n: "2", t: "Pass the safety gate", d: "Medicines, conditions or surgery flags switch the plan to clinician-safe mode automatically." },
              { n: "3", t: "Get your 10-point plan", d: "Ordered cards: food foundations first, at most two evidence-graded supplement decisions, and a review loop." },
            ].map((s, i) => (
              <Reveal key={s.n} delay={i * 0.1}>
                <div className="rounded-md border-2 border-ink/12 bg-bone p-7 h-full hover:border-ink transition-colors duration-300" data-testid={`how-step-${s.n}`}>
                  <span className="font-serif text-6xl font-extrabold leading-none text-clay block mb-4">{s.n}</span>
                  <h3 className="font-sans text-lg font-extrabold uppercase tracking-wide mb-2">{s.t}</h3>
                  <p className="text-sm text-slatem leading-relaxed">{s.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <div className="mt-12 text-center">
              <Link to="/check-in" data-testid="how-cta-button"
                className="group inline-flex items-center gap-2.5 rounded-md bg-ink text-bone px-8 py-4 font-sans text-sm sm:text-base font-bold uppercase tracking-wider transition-colors duration-200 hover:bg-clay min-h-[44px]">
                Start the test <Flame className="h-4 w-4 transition-transform duration-200 group-hover:scale-110" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* LATEST INSIGHTS */}
      {insights.length > 0 && (
        <section className="px-4 sm:px-6 pb-20 sm:pb-28">
          <div className="mx-auto max-w-7xl">
            <Reveal>
              <div className="border-t-[3px] border-ink pt-4 flex items-end justify-between gap-6">
                <div>
                  <p className="font-sans text-xs sm:text-sm font-extrabold uppercase tracking-[0.18em] text-clay mb-2">The latest</p>
                  <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-extrabold uppercase tracking-tight leading-[0.98]" data-testid="insights-preview-heading">
                    Research, minus the hype.
                  </h2>
                </div>
                <Link to="/insights" data-testid="insights-preview-all" className="hidden sm:inline-flex items-center gap-1.5 font-sans text-sm font-bold uppercase tracking-wider text-ink/70 hover:text-clay transition-colors shrink-0 mb-1">
                  All insights <ArrowUpRight className="h-4 w-4" />
                </Link>
              </div>
            </Reveal>
            <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {insights.slice(0, 3).map((a, i) => (
                <Reveal key={a.slug} delay={i * 0.08}>
                  <Link to={`/insights/${a.slug}`} data-testid={`insight-card-${a.slug}`} className="group block h-full">
                    <div className="overflow-hidden rounded-sm border-2 border-ink/10 aspect-[4/3] mb-4">
                      <img src={a.image} alt={a.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                    </div>
                    <p className="font-sans text-[11px] font-extrabold uppercase tracking-[0.18em] text-clay mb-2">
                      {a.category} · Grade {a.grade} · {a.read_minutes} min
                    </p>
                    <h3 className="font-serif text-xl sm:text-2xl font-bold uppercase tracking-tight leading-tight group-hover:text-clay transition-colors duration-200">{a.title}</h3>
                  </Link>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA BAND */}
      <section className="px-4 sm:px-6 pb-24">
        <div className="mx-auto max-w-7xl">
          <Reveal>
            <div className="relative overflow-hidden rounded-md bg-ink text-bone px-6 sm:px-14 py-16 sm:py-20 text-center zb-grain border-2 border-ink" data-testid="cta-band">
              <p className="font-sans text-xs sm:text-sm font-extrabold uppercase tracking-[0.18em] text-sage mb-5">The test takes two minutes</p>
              <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-extrabold uppercase tracking-tight leading-[0.95] text-balance max-w-3xl mx-auto">
                How close are you to Zinda Banda? <span className="text-clay">Find out.</span>
              </h2>
              <Link to="/check-in" data-testid="cta-band-button"
                className="group mt-9 inline-flex items-center gap-2.5 rounded-md bg-clay text-bone px-8 py-4 font-sans text-sm sm:text-base font-bold uppercase tracking-wider transition-all duration-200 hover:bg-bone hover:text-ink min-h-[44px]">
                Test if you are Zinda Banda
                <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" />
              </Link>
              <p className="mt-6 font-mono text-[10px] uppercase tracking-[0.2em] text-bone/50">anonymous · 2 minutes · nothing to buy</p>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
